/**
 * pmo #23485 (letra de la CTO, opción [B]) — un tenant cuyo plan no trae `soulbuilder` no tiene sitios: `GET /multi-site/sites` le contesta 403
 * `PLAN_FEATURE_MISSING` (`PlanFeatureGuard` + `@RequireFeature('soulbuilder')`, que el servidor lee de `subscription.plan.features`). El front NO
 * conoce las features del plan al cargar el layout (no vienen en el login ni en ningún slice), así que no puede evitar la primera consulta; lo que sí
 * puede es no repetirla ni mostrarla como un fallo.
 *
 *   - `PLAN_FEATURE_MISSING` se lee como «este tenant no tiene sitios»: lista vacía, sin error visible.
 *   - Se recuerda por tenant y por SESIÓN (sessionStorage, con respaldo en memoria si el navegador no deja usarlo): a lo sumo UNA consulta con 403
 *     por tenant y sesión, y ninguna en las páginas siguientes.
 *   - CUALQUIER OTRO 403 (sin suscripción, sin tenant, rol) o 5xx sigue siendo un error: no se traga. Un 403 de otra causa NO se recuerda.
 */
const PREFIJO = 'inbomarket_sitios_sin_plan:';
const enMemoria = new Set<string>();

const clave = (tenantId: string) => `${PREFIJO}${tenantId}`;

/** ¿El error es el 403 de «tu plan no incluye esto»? Solo ese; el código viaja en la raíz del cuerpo (o dentro de `message`, según el filtro). */
export const esPlanSinSitios = (error: any): boolean => {
  if (error?.response?.status !== 403) return false;
  const cuerpo = error.response.data;
  return cuerpo?.code === 'PLAN_FEATURE_MISSING' || cuerpo?.message?.code === 'PLAN_FEATURE_MISSING';
};

export const tenantSinSitios = (tenantId?: string | null): boolean => {
  if (!tenantId) return false;
  if (enMemoria.has(tenantId)) return true;
  try {
    return window.sessionStorage.getItem(clave(tenantId)) === '1';
  } catch {
    return false;
  }
};

export const recordarTenantSinSitios = (tenantId?: string | null): void => {
  if (!tenantId) return;
  enMemoria.add(tenantId);
  try {
    window.sessionStorage.setItem(clave(tenantId), '1');
  } catch {
    // sin sessionStorage (modo privado): queda en memoria, que dura hasta recargar
  }
};

export const olvidarTenantSinSitios = (tenantId?: string | null): void => {
  if (!tenantId) return;
  enMemoria.delete(tenantId);
  try {
    window.sessionStorage.removeItem(clave(tenantId));
  } catch {
    // ignore
  }
};
